<script>
import { mapState } from "vuex";
import AvatarCropper from "vue-avatar-cropper";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import Layout from "@/router/layouts/main";
import EmptyState from "@/components/widgets/empty-state";
import StPageHeader from "@/components/base/st-page-header";
import { api } from "@/state/services";
import { isSecretaria } from "@/utils/minhas-metas";
import { IMG_BRASAO, IMG_PIN, IMG_FORMADOR } from "@/utils/jornada";
import { removerFundo, aplicarEfeito3d, canvasParaBlob } from "@/utils/recorte-foto";

/**
 * Formatos do cartão: proporção na tela e largura final do arquivo exportado.
 *   retrato   1080x1350  post e status
 *   quadrado  1080x1080  foto de perfil
 *   retangulo 1920x1080  cabeçalho, apresentação, envio no WhatsApp
 */
const FORMATOS = {
  retrato: { rotulo: "Retrato", uso: "Post e status", largura: 1080 },
  quadrado: { rotulo: "Quadrado", uso: "Foto de perfil", largura: 1080 },
  retangulo: { rotulo: "Retângulo", uso: "Cabeçalho e WhatsApp", largura: 1920 },
};

/** Recorte da foto (4:5, da cintura/peito para cima). */
const RECORTE = { width: 600, height: 750 };

/**
 * Meu Cartão — cartão de visita de conquistas: foto profissional SEM FUNDO (separada do avatar),
 * nome, brasão da carreira e as conquistas JÁ ATINGIDAS (PINs da Placa e Formador). Nada do que
 * falta atingir aparece no cartão; qualificação não entra (é do mês).
 *
 * Foto: recorte 4:5 (vue-avatar-cropper) -> remoção de fundo no navegador (MediaPipe, ver
 * utils/recorte-foto.js) -> efeito 3D gravado nos pixels -> PNG enviado à API. Se a remoção
 * falhar, a pessoa pode usar a foto com fundo (JPEG) — o cartão mostra em moldura.
 * Exportação PNG/PDF no navegador (html2canvas + jsPDF); todas as imagens são data URL ou assets
 * locais, então o canvas não é bloqueado.
 */
export default {
  components: { Layout, EmptyState, StPageHeader, AvatarCropper },
  data() {
    return {
      FORMATOS,
      cartao: null,
      disponivel: true,
      erro: "",
      carregando: false,
      enviandoFoto: false,
      msgFoto: "",
      exportando: false,
      formato: "retrato",
      cropperOpcoes: { aspectRatio: RECORTE.width / RECORTE.height, viewMode: 1, autoCropArea: 1 },
      cropperLabels: { submit: "Continuar", cancel: "Cancelar" },
      logo: require("@/assets/images/logo-white.png"),
      podeCompartilhar: false,
      dicasAbertas: false,
      // Modal de tratamento da foto
      modalFoto: false,
      processando: false,
      falhaRecorte: "",
      canvasOriginal: null,
      canvasRecorte: null,
      previewRecorte: "",
      previewOriginal: "",
      usarSemFundo: true,
    };
  },
  computed: {
    ...mapState({ account: (state) => state.account }),
    user() {
      return (this.account && this.account.user) || null;
    },
    secretaria() {
      return isSecretaria(this.user);
    },
    nome() {
      const n = (this.cartao && this.cartao.nome) || (this.user && this.user.name) || "";
      return n.toUpperCase();
    },
    iniciais() {
      const partes = this.nome.split(/\s+/).filter(Boolean);
      if (!partes.length) return "?";
      return (partes[0][0] + (partes.length > 1 ? partes[partes.length - 1][0] : "")).toUpperCase();
    },
    // Foto sem fundo é sempre PNG; a com fundo (fallback) é JPEG.
    fotoRecortada() {
      return !!(this.cartao && this.cartao.foto && this.cartao.foto.indexOf("data:image/png") === 0);
    },
    carreira() {
      return (this.cartao && this.cartao.carreira) || { key: "consultor", label: "Consultor" };
    },
    imgCarreira() {
      return IMG_BRASAO[this.carreira.key] || IMG_BRASAO.consultor;
    },
    // Só o que JÁ foi conquistado.
    pins() {
      const lista = (this.cartao && this.cartao.pins) || [];
      return lista.filter((n) => IMG_PIN[n]).map((n) => ({ nivel: n, img: IMG_PIN[n] }));
    },
    formador() {
      const lista = (this.cartao && this.cartao.formador) || [];
      return lista.filter((n) => IMG_FORMADOR[n]).map((n) => ({ nivel: n, img: IMG_FORMADOR[n] }));
    },
    temConquistas() {
      return this.pins.length > 0 || this.formador.length > 0;
    },
    regional() {
      return this.cartao && this.cartao.regional ? "Regional " + this.titulo(this.cartao.regional) : "";
    },
    nomeArquivo() {
      const base = (this.nome || "cartao")
        .normalize("NFD")
        .replace(/[^\x20-\x7e]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      return "cartao-" + (base || "teams") + "-" + this.formato;
    },
  },
  created() {
    this.carregar();
    try {
      const teste = new File([""], "t.png", { type: "image/png" });
      this.podeCompartilhar = !!(navigator.canShare && navigator.canShare({ files: [teste] }));
    } catch (e) {
      this.podeCompartilhar = false;
    }
  },
  methods: {
    titulo(texto) {
      return String(texto || "")
        .toLowerCase()
        .replace(/(^|\s)\S/g, (l) => l.toUpperCase());
    },
    fmtNum(v) {
      return Number(v || 0).toLocaleString("pt-BR");
    },
    carregar() {
      this.carregando = true;
      this.erro = "";
      api
        .get("cartao")
        .then((response) => {
          const d = response && response.data;
          if (!d || d.status !== "success") {
            this.erro = (d && d.message) || "Não foi possível carregar o seu cartão.";
            return;
          }
          this.cartao = d.cartao;
          this.disponivel = d.disponivel !== false;
          this.dicasAbertas = !d.cartao.foto;
        })
        .catch(() => {
          this.erro = "Não foi possível carregar o seu cartão. Verifique a conexão e tente de novo.";
        })
        .finally(() => {
          this.carregando = false;
        });
    },

    // ===== Foto =====
    // vue-avatar-cropper chama este handler com o cropper ANTES de se destruir: o canvas
    // recortado precisa ser tirado aqui, de forma síncrona.
    aoRecortar(cropper) {
      const canvas = cropper.getCroppedCanvas({
        width: RECORTE.width,
        height: RECORTE.height,
        fillColor: "#ffffff",
        imageSmoothingQuality: "high",
      });
      if (!canvas) {
        this.msgFoto = "Não foi possível ler a foto. Tente outra imagem.";
        return;
      }
      this.canvasOriginal = canvas;
      this.previewOriginal = canvas.toDataURL("image/jpeg", 0.9);
      this.canvasRecorte = null;
      this.previewRecorte = "";
      this.falhaRecorte = "";
      this.usarSemFundo = true;
      this.modalFoto = true;
      this.processarRecorte();
    },
    async processarRecorte() {
      this.processando = true;
      try {
        const semFundo = await removerFundo(this.canvasOriginal);
        this.canvasRecorte = aplicarEfeito3d(semFundo);
        this.previewRecorte = this.canvasRecorte.toDataURL("image/png");
      } catch (e) {
        this.usarSemFundo = false;
        this.falhaRecorte =
          e && e.message === "pessoa não encontrada"
            ? "Não encontramos uma pessoa na foto. Tente uma foto da cintura para cima, de frente."
            : "Não foi possível remover o fundo neste aparelho. Você pode usar a foto com fundo ou tentar de outro navegador.";
      } finally {
        this.processando = false;
      }
    },
    fecharModalFoto() {
      this.modalFoto = false;
      this.canvasOriginal = null;
      this.canvasRecorte = null;
      this.previewRecorte = "";
      this.previewOriginal = "";
    },
    async salvarFoto() {
      const semFundo = this.usarSemFundo && this.canvasRecorte;
      const canvas = semFundo ? this.canvasRecorte : this.canvasOriginal;
      if (!canvas) return;
      this.enviandoFoto = true;
      this.msgFoto = "";
      try {
        const blob = semFundo ? await canvasParaBlob(canvas, "image/png") : await canvasParaBlob(canvas, "image/jpeg", 0.9);
        const form = new FormData();
        form.append("foto", blob, semFundo ? "cartao.png" : "cartao.jpg");
        const response = await api.post("cartao/foto", form);
        const d = response && response.data;
        if (d && d.status === "success") {
          this.cartao = { ...this.cartao, foto: d.foto || null };
          this.dicasAbertas = false;
          this.fecharModalFoto();
        } else {
          this.msgFoto = (d && d.message) || "Não foi possível salvar a foto. Tente de novo.";
        }
      } catch (e) {
        this.msgFoto = "Não foi possível enviar a foto. Verifique a conexão e tente de novo.";
      } finally {
        this.enviandoFoto = false;
      }
    },
    removerFoto() {
      this.enviandoFoto = true;
      this.msgFoto = "";
      api
        .delete("cartao/foto")
        .then((response) => {
          const d = response && response.data;
          if (d && d.status === "success") {
            this.cartao = { ...this.cartao, foto: null };
          } else {
            this.msgFoto = (d && d.message) || "Não foi possível remover a foto.";
          }
        })
        .catch(() => {
          this.msgFoto = "Não foi possível remover a foto. Tente de novo.";
        })
        .finally(() => {
          this.enviandoFoto = false;
        });
    },

    // ===== Exportação =====
    async gerarCanvas() {
      this.exportando = true;
      await this.$nextTick();
      const el = this.$refs.cartao;
      try {
        return await html2canvas(el, {
          scale: FORMATOS[this.formato].largura / el.offsetWidth,
          backgroundColor: null,
          useCORS: true,
          logging: false,
        });
      } finally {
        this.exportando = false;
      }
    },
    baixar(url, nome) {
      const a = document.createElement("a");
      a.href = url;
      a.download = nome;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    avisarFalha() {
      this.$bvToast.toast("Não foi possível gerar o arquivo. Tente de novo.", {
        title: "Meu Cartão",
        variant: "danger",
        solid: true,
      });
    },
    async exportarPng() {
      try {
        const canvas = await this.gerarCanvas();
        this.baixar(canvas.toDataURL("image/png"), this.nomeArquivo + ".png");
      } catch (e) {
        this.avisarFalha();
      }
    },
    async exportarPdf() {
      try {
        const canvas = await this.gerarCanvas();
        const w = canvas.width;
        const h = canvas.height;
        const pdf = new jsPDF({ orientation: w > h ? "l" : "p", unit: "px", format: [w, h], hotfixes: ["px_scaling"] });
        pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, w, h);
        pdf.save(this.nomeArquivo + ".pdf");
      } catch (e) {
        this.avisarFalha();
      }
    },
    async compartilhar() {
      try {
        const canvas = await this.gerarCanvas();
        const blob = await canvasParaBlob(canvas, "image/png");
        const arquivo = new File([blob], this.nomeArquivo + ".png", { type: "image/png" });
        await navigator.share({ files: [arquivo], title: "Meu Cartão" });
      } catch (e) {
        // Usuário cancelou o compartilhamento: não é erro.
        if (e && e.name !== "AbortError") this.avisarFalha();
      }
    },
  },
};
</script>

<template>
  <Layout>
    <div class="mc">
      <StPageHeader
        title="Meu Cartão"
        subtitle="Seu cartão de visita com as conquistas da sua carreira. Baixe e compartilhe nas redes sociais."
        :breadcrumb="[{ text: 'Meu Cartão' }]"
      />

      <div v-if="secretaria" class="card">
        <div class="card-body">
          <EmptyState icon="bx bx-id-card" title="Cartão pessoal" text="O cartão de visita é pessoal da titular da conta." />
        </div>
      </div>

      <div v-else-if="erro && !cartao" class="card">
        <div class="card-body">
          <EmptyState icon="bx bx-error-circle" title="Não foi possível carregar o seu cartão" :text="erro">
            <template #action>
              <button type="button" class="btn btn-default" @click="carregar">Tentar de novo</button>
            </template>
          </EmptyState>
        </div>
      </div>

      <div v-else-if="carregando && !cartao" class="mc-grid" aria-busy="true">
        <div class="mc-sk"></div>
      </div>

      <div v-else-if="cartao" class="mc-grid" :class="'mc-grid-' + formato">
        <!-- Cartão (é exatamente o que vai para o PNG/PDF) -->
        <div class="mc-preview">
          <div
            ref="cartao"
            class="mc-cartao"
            :class="['mc-' + formato, { 'mc-exportando': exportando, 'mc-sem-conquistas': !temConquistas }]"
          >
            <div class="mc-brilho" aria-hidden="true"></div>

            <!-- Foto: sem fundo "saindo" do painel (3D); com fundo em moldura; sem foto, iniciais -->
            <div class="mc-area-foto">
              <div class="mc-palco" :class="{ 'mc-palco-recorte': fotoRecortada }">
                <div v-if="fotoRecortada" class="mc-palco-fundo" aria-hidden="true"></div>
                <img
                  v-if="cartao.foto"
                  :src="cartao.foto"
                  alt="Foto do cartão"
                  :class="fotoRecortada ? 'mc-foto-recorte' : 'mc-foto-moldura'"
                />
                <span v-else class="mc-iniciais">{{ iniciais }}</span>
              </div>
            </div>

            <div class="mc-info">
              <h2 class="mc-nome">{{ nome }}</h2>
              <p v-if="regional" class="mc-sub">{{ regional }}</p>

              <div class="mc-carreira">
                <img :src="imgCarreira" :alt="carreira.label" class="mc-brasao" />
                <div class="mc-carreira-texto">
                  <span class="mc-rotulo">Carreira</span>
                  <strong>{{ carreira.label }}</strong>
                </div>
              </div>

              <!-- Conquistas separadas por título; grupo sem conquista não aparece -->
              <div v-if="temConquistas" class="mc-conquistas">
                <div v-if="pins.length" class="mc-grupo">
                  <span class="mc-grupo-titulo">Placas Ativas</span>
                  <ul class="mc-pins">
                    <li v-for="p in pins" :key="'pin' + p.nivel">
                      <img :src="p.img" :alt="'PIN ' + p.nivel" />
                    </li>
                  </ul>
                  <p v-if="cartao.base_historica" class="mc-base">{{ fmtNum(cartao.base_historica) }} placas ativas</p>
                </div>
                <div v-if="formador.length" class="mc-grupo">
                  <span class="mc-grupo-titulo">Formação de Líderes</span>
                  <ul class="mc-pins">
                    <li v-for="f in formador" :key="'form' + f.nivel">
                      <img :src="f.img" :alt="'Formador ' + f.nivel" />
                    </li>
                  </ul>
                  <p v-if="cartao.lideres_formados" class="mc-base">
                    {{ fmtNum(cartao.lideres_formados) }}
                    {{ cartao.lideres_formados === 1 ? "Consultor Líder Formado" : "Consultores Líderes Formados" }}
                  </p>
                </div>
              </div>

              <div class="mc-rodape">
                <img :src="logo" alt="Teams Connect" class="mc-logo" />
              </div>
            </div>
          </div>
        </div>

        <!-- Ações -->
        <div class="mc-acoes">
          <section class="card">
            <div class="card-body">
              <h3 class="mc-h3">Foto do cartão</h3>
              <p class="text-muted small">
                Envie uma foto sua: o fundo é removido automaticamente e fica só você, com efeito 3D. Ela é
                separada da foto do seu perfil.
              </p>
              <div v-if="!disponivel" class="alert alert-warning small mb-2">
                O envio de foto ainda não está disponível. Tente mais tarde.
              </div>
              <div class="d-flex flex-wrap" style="gap: 0.5rem">
                <button id="mc-trocar-foto" type="button" class="btn btn-default" :disabled="enviandoFoto || !disponivel">
                  <b-spinner v-if="enviandoFoto" small class="mr-1" />
                  <i v-else class="bx bx-camera mr-1" aria-hidden="true"></i>
                  {{ cartao.foto ? "Trocar foto" : "Enviar foto" }}
                </button>
                <button v-if="cartao.foto" type="button" class="btn btn-outline-danger" :disabled="enviandoFoto" @click="removerFoto">
                  <i class="bx bx-trash mr-1" aria-hidden="true"></i> Remover
                </button>
              </div>
              <p v-if="msgFoto && !modalFoto" class="text-danger small mt-2 mb-0" role="alert">{{ msgFoto }}</p>
              <avatar-cropper
                v-if="disponivel"
                trigger="#mc-trocar-foto"
                :upload-handler="aoRecortar"
                :cropper-options="cropperOpcoes"
                :labels="cropperLabels"
                mimes="image/png, image/jpeg, image/jpg, image/webp"
              />

              <!-- Dicas -->
              <button
                type="button"
                class="btn btn-link px-0 mt-2 mc-dicas-toggle"
                :aria-expanded="dicasAbertas ? 'true' : 'false'"
                aria-controls="mc-dicas"
                @click="dicasAbertas = !dicasAbertas"
              >
                <i class="bx bx-bulb" aria-hidden="true"></i> Como tirar a foto ideal
                <i class="bx" :class="dicasAbertas ? 'bx-chevron-up' : 'bx-chevron-down'" aria-hidden="true"></i>
              </button>
              <b-collapse id="mc-dicas" v-model="dicasAbertas">
                <ol class="mc-dicas">
                  <li>
                    <i class="bx bx-user-pin" aria-hidden="true"></i>
                    <div>
                      <strong>Enquadramento</strong>
                      <span>Da cintura ou do peito para cima, de frente, rosto no centro e olhando para a câmera. Deixe um espaço acima da cabeça.</span>
                    </div>
                  </li>
                  <li>
                    <i class="bx bx-sun" aria-hidden="true"></i>
                    <div>
                      <strong>Luz</strong>
                      <span>Fique de frente para uma janela ou luz clara. Evite luz atrás de você (contraluz) e sombras fortes no rosto.</span>
                    </div>
                  </li>
                  <li>
                    <i class="bx bx-rectangle" aria-hidden="true"></i>
                    <div>
                      <strong>Fundo</strong>
                      <span>Parede lisa e de cor diferente da sua roupa e do seu cabelo. Isso deixa o recorte bem mais caprichado.</span>
                    </div>
                  </li>
                  <li>
                    <i class="bx bx-closet" aria-hidden="true"></i>
                    <div>
                      <strong>Roupa</strong>
                      <span>Social ou camisa da empresa, em cor lisa. Evite estampas muito miúdas e roupa da mesma cor da parede.</span>
                    </div>
                  </li>
                  <li>
                    <i class="bx bx-mobile-alt" aria-hidden="true"></i>
                    <div>
                      <strong>Celular</strong>
                      <span>Peça para alguém tirar com a câmera traseira, na altura dos seus olhos, a uns 1,5 m. Não use zoom, filtro nem efeito de beleza.</span>
                    </div>
                  </li>
                  <li>
                    <i class="bx bx-happy-beaming" aria-hidden="true"></i>
                    <div>
                      <strong>Postura</strong>
                      <span>Ombros relaxados, corpo levemente de lado e sorriso natural. Sem boné nem óculos escuros.</span>
                    </div>
                  </li>
                </ol>
                <div class="mc-dicas-ok">
                  <span><i class="bx bx-check-circle" aria-hidden="true"></i> Foto nítida, bem iluminada, fundo liso</span>
                  <span class="mc-evite"><i class="bx bx-x-circle" aria-hidden="true"></i> Selfie de longe, contraluz, fundo bagunçado, várias pessoas</span>
                </div>
              </b-collapse>
            </div>
          </section>

          <section class="card">
            <div class="card-body">
              <h3 class="mc-h3">Formato</h3>
              <div class="mc-formatos" role="group" aria-label="Formato do cartão">
                <button
                  v-for="(f, chave) in FORMATOS"
                  :key="chave"
                  type="button"
                  class="mc-formato"
                  :class="{ ativo: formato === chave }"
                  :aria-pressed="formato === chave ? 'true' : 'false'"
                  @click="formato = chave"
                >
                  <span class="mc-formato-icone" :class="'mc-formato-' + chave" aria-hidden="true"></span>
                  <strong>{{ f.rotulo }}</strong>
                  <small>{{ f.uso }}</small>
                </button>
              </div>
            </div>
          </section>

          <section class="card">
            <div class="card-body">
              <h3 class="mc-h3">Exportar</h3>
              <div class="d-flex flex-column" style="gap: 0.5rem">
                <button type="button" class="btn btn-default" :disabled="exportando" @click="exportarPng">
                  <i class="bx bx-image mr-1" aria-hidden="true"></i> Baixar imagem (PNG)
                </button>
                <button type="button" class="btn btn-outline-default" :disabled="exportando" @click="exportarPdf">
                  <i class="bx bxs-file-pdf mr-1" aria-hidden="true"></i> Baixar PDF
                </button>
                <button v-if="podeCompartilhar" type="button" class="btn btn-outline-default" :disabled="exportando" @click="compartilhar">
                  <i class="bx bx-share-alt mr-1" aria-hidden="true"></i> Compartilhar
                </button>
              </div>
              <p class="text-muted small mt-2 mb-0">
                O cartão mostra só o que você já conquistou: sua carreira, os PINs da Placa e os brasões de Formador.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>

    <!-- Tratamento da foto (remoção de fundo) -->
    <b-modal
      :visible="modalFoto"
      title="Sua foto do cartão"
      centered
      no-close-on-backdrop
      :hide-footer="processando"
      @hidden="fecharModalFoto"
    >
      <div v-if="processando" class="mc-proc" role="status">
        <b-spinner variant="primary" />
        <strong>Removendo o fundo da foto…</strong>
        <span class="text-muted small">Na primeira vez pode levar alguns segundos.</span>
      </div>
      <template v-else>
        <div class="mc-comparar">
          <button
            v-if="previewRecorte"
            type="button"
            class="mc-opcao"
            :class="{ ativo: usarSemFundo }"
            :aria-pressed="usarSemFundo ? 'true' : 'false'"
            @click="usarSemFundo = true"
          >
            <span class="mc-xadrez"><img :src="previewRecorte" alt="Foto sem fundo" /></span>
            <strong>Sem fundo (3D)</strong>
          </button>
          <button
            type="button"
            class="mc-opcao"
            :class="{ ativo: !usarSemFundo }"
            :aria-pressed="!usarSemFundo ? 'true' : 'false'"
            @click="usarSemFundo = false"
          >
            <span class="mc-xadrez"><img :src="previewOriginal" alt="Foto com fundo" /></span>
            <strong>Com fundo</strong>
          </button>
        </div>
        <p v-if="falhaRecorte" class="alert alert-warning small mt-3 mb-0">{{ falhaRecorte }}</p>
        <p v-else class="text-muted small mt-3 mb-0">
          O recorte ficou estranho? Tente outra foto seguindo as dicas: fundo liso, boa luz e da cintura para cima.
        </p>
        <p v-if="msgFoto" class="text-danger small mt-2 mb-0" role="alert">{{ msgFoto }}</p>
      </template>
      <template #modal-footer>
        <button type="button" class="btn btn-light" :disabled="enviandoFoto" @click="fecharModalFoto">Cancelar</button>
        <button type="button" class="btn btn-default" :disabled="enviandoFoto" @click="salvarFoto">
          <b-spinner v-if="enviandoFoto" small class="mr-1" /> Usar esta foto
        </button>
      </template>
    </b-modal>
  </Layout>
</template>

<style scoped>
.mc-grid {
  display: grid;
  grid-template-columns: minmax(0, 440px) minmax(0, 1fr);
  gap: 1.5rem;
  align-items: start;
}
.mc-grid-retangulo {
  grid-template-columns: minmax(0, 1fr);
}
.mc-grid-retangulo .mc-acoes {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}
.mc-grid-retangulo .mc-acoes .card {
  margin-bottom: 0;
}
.mc-sk {
  width: 100%;
  max-width: 440px;
  aspect-ratio: 4 / 5;
  border-radius: 20px;
  background: linear-gradient(90deg, var(--st-canvas), var(--st-border-soft), var(--st-canvas));
  background-size: 200% 100%;
  animation: mc-shimmer 1.2s ease-in-out infinite;
}
@keyframes mc-shimmer {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}
.mc-h3 {
  margin: 0 0 0.5rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--st-text);
}
.mc-acoes .card {
  margin-bottom: 1rem;
}

/* ===== Cartão (cores fixas: o arquivo exportado não depende do tema claro/escuro) ===== */
.mc-cartao {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 440px;
  /* Retrato: altura segue o conteúdo — sem conquistas o cartão fica mais baixo, sem espaço vazio.
     Quadrado e retângulo mantêm a proporção (foto de perfil / cabeçalho) e centralizam o conteúdo. */
  padding: 22px 24px 16px;
  overflow: hidden;
  border-radius: 20px;
  background: linear-gradient(160deg, #123a7a 0%, #0b2a5b 45%, #06163a 100%);
  box-shadow: 0 12px 32px rgba(6, 22, 58, 0.35);
  color: #fff;
  text-align: center;
}
.mc-cartao.mc-quadrado {
  justify-content: center;
  aspect-ratio: 1 / 1;
  padding: 16px 20px 10px;
}
.mc-quadrado .mc-info {
  flex: 0 0 auto;
}
.mc-quadrado .mc-rodape {
  margin-top: 10px;
}
/* Sem conquistas: a foto cresce para ocupar o espaço (quadrado e retângulo têm altura fixa). */
.mc-quadrado.mc-sem-conquistas .mc-palco {
  width: 52%;
}
.mc-retangulo.mc-sem-conquistas .mc-nome {
  font-size: 40px;
}
.mc-cartao.mc-retangulo {
  flex-direction: row;
  align-items: stretch;
  max-width: 880px;
  aspect-ratio: 16 / 9;
  padding: 0 36px 0 0;
  text-align: left;
}
.mc-cartao.mc-exportando {
  border-radius: 0;
  box-shadow: none;
}
.mc-brilho {
  position: absolute;
  top: -30%;
  left: 50%;
  width: 140%;
  height: 75%;
  transform: translateX(-50%);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(245, 197, 66, 0.3) 0%, rgba(245, 197, 66, 0) 70%);
  pointer-events: none;
}
.mc-retangulo .mc-brilho {
  top: 0;
  left: 22%;
  width: 70%;
  height: 130%;
}

/* Foto */
.mc-area-foto {
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
}
.mc-retangulo .mc-area-foto {
  flex: 0 0 44%;
  align-items: flex-end;
}
.mc-palco {
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 58%;
}
.mc-quadrado .mc-palco {
  width: 40%;
}
.mc-retangulo .mc-palco {
  width: 86%;
}
/* Painel atrás da pessoa: mais baixo que a foto, para a cabeça "sair" dele (efeito 3D). */
.mc-palco-fundo {
  position: absolute;
  bottom: 0;
  left: 4%;
  right: 4%;
  height: 70%;
  border: 2px solid rgba(247, 215, 116, 0.55);
  border-bottom: 0;
  border-radius: 22px 22px 0 0;
  background: linear-gradient(180deg, rgba(247, 215, 116, 0.35) 0%, rgba(29, 74, 143, 0.55) 100%);
}
.mc-retangulo .mc-palco-fundo {
  height: 66%;
  border-radius: 28px 28px 0 0;
}
.mc-foto-recorte {
  position: relative;
  display: block;
  width: 100%;
  height: auto;
}
.mc-foto-moldura {
  display: block;
  width: 86%;
  height: auto;
  border: 3px solid #f7d774;
  border-radius: 16px;
}
.mc-retangulo .mc-foto-moldura {
  width: 76%;
  margin: auto 0 36px;
}
.mc-iniciais {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 128px;
  height: 128px;
  border: 3px solid #f7d774;
  border-radius: 24px;
  background: #1d4a8f;
  font-size: 44px;
  font-weight: 800;
  color: #f7d774;
}
.mc-quadrado .mc-iniciais {
  width: 96px;
  height: 96px;
  font-size: 34px;
}
.mc-retangulo .mc-iniciais {
  width: 180px;
  height: 180px;
  margin: auto 0;
  font-size: 64px;
}
.mc-retangulo .mc-palco:not(.mc-palco-recorte) {
  align-self: center;
}

/* Informações */
.mc-info {
  position: relative;
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  width: 100%;
  border-top: 2px solid rgba(247, 215, 116, 0.55);
  padding-top: 12px;
}
.mc-retangulo .mc-info {
  flex: 1 1 auto;
  align-items: flex-start;
  justify-content: center;
  padding: 28px 0 18px;
  border-top: 0;
}
.mc-nome {
  margin: 0 0 2px;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: 0.02em;
  color: #fff;
}
.mc-quadrado .mc-nome {
  font-size: 18px;
}
.mc-retangulo .mc-nome {
  font-size: 34px;
}
.mc-sub {
  margin: 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.72);
}
.mc-retangulo .mc-sub {
  font-size: 15px;
}
.mc-carreira {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  padding: 6px 18px 6px 10px;
  border: 1px solid rgba(247, 215, 116, 0.45);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
}
.mc-quadrado .mc-carreira {
  margin-top: 8px;
  padding: 3px 14px 3px 8px;
}
.mc-retangulo .mc-carreira {
  margin-top: 18px;
  padding: 8px 22px 8px 12px;
}
.mc-brasao {
  width: 56px;
  height: auto;
}
.mc-quadrado .mc-brasao {
  width: 42px;
}
.mc-retangulo .mc-brasao {
  width: 76px;
}
.mc-carreira-texto {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}
.mc-carreira-texto strong {
  font-size: 17px;
  font-weight: 800;
  color: #f7d774;
}
.mc-retangulo .mc-carreira-texto strong {
  font-size: 22px;
}
.mc-rotulo {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);
}
.mc-conquistas {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 10px;
}
.mc-retangulo .mc-conquistas {
  flex: 0 0 auto;
  align-items: flex-start;
  margin-top: 22px;
}
.mc-grupo {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
}
.mc-grupo + .mc-grupo {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}
.mc-grupo-titulo {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #f7d774;
}
.mc-retangulo .mc-grupo {
  align-items: flex-start;
}
.mc-quadrado .mc-grupo + .mc-grupo {
  margin-top: 4px;
  padding-top: 4px;
}
.mc-pins {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.mc-retangulo .mc-pins {
  justify-content: flex-start;
}
.mc-pins img {
  display: block;
  width: 52px;
  height: auto;
}
.mc-quadrado .mc-pins img {
  width: 40px;
}
.mc-retangulo .mc-pins img {
  width: 64px;
}
.mc-base {
  margin: 0;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.65);
}
.mc-rodape {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 16px;
}
.mc-retangulo .mc-rodape {
  justify-content: flex-start;
  margin-top: 24px;
}
.mc-logo {
  width: 110px;
  height: auto;
  opacity: 0.9;
}
.mc-quadrado .mc-logo {
  width: 88px;
}
.mc-retangulo .mc-logo {
  width: 140px;
}

/* ===== Formato ===== */
.mc-formatos {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
}
.mc-formato {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.125rem;
  padding: 0.75rem 0.375rem;
  border: 2px solid var(--st-border-soft);
  border-radius: var(--st-radius-lg);
  background: var(--st-surface);
  color: var(--st-text);
  text-align: center;
}
.mc-formato small {
  color: var(--st-text-muted);
  line-height: 1.2;
}
.mc-formato.ativo {
  border-color: var(--st-brand);
  background: var(--st-brand-soft);
}
.mc-formato:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--st-focus-ring);
}
.mc-formato-icone {
  display: block;
  height: 28px;
  margin-bottom: 0.25rem;
  border: 2px solid currentColor;
  border-radius: 4px;
  opacity: 0.6;
}
.mc-formato-retrato { width: 22px; }
.mc-formato-quadrado { width: 28px; }
.mc-formato-retangulo { width: 44px; height: 25px; margin-top: 3px; }

/* ===== Dicas ===== */
.mc-dicas-toggle {
  font-weight: 600;
  text-decoration: none;
}
.mc-dicas {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  margin: 0.25rem 0 0.75rem;
  padding: 0;
  list-style: none;
}
.mc-dicas li {
  display: flex;
  gap: 0.625rem;
  font-size: 0.8125rem;
}
.mc-dicas li > i {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: var(--st-brand-soft);
  color: var(--st-brand);
  font-size: 1.125rem;
}
.mc-dicas strong {
  display: block;
  color: var(--st-text);
}
.mc-dicas span {
  color: var(--st-text-muted);
}
.mc-dicas-ok {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.8125rem;
  color: var(--st-success);
}
.mc-dicas-ok .mc-evite {
  color: var(--st-danger, #d64545);
}

/* ===== Modal da foto ===== */
.mc-proc {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem 0;
  text-align: center;
}
.mc-comparar {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  gap: 0.75rem;
}
.mc-opcao {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem;
  border: 2px solid var(--st-border-soft);
  border-radius: var(--st-radius-lg);
  background: var(--st-surface);
  color: var(--st-text);
}
.mc-opcao.ativo {
  border-color: var(--st-brand);
  background: var(--st-brand-soft);
}
.mc-opcao:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--st-focus-ring);
}
.mc-xadrez {
  display: block;
  width: 100%;
  border-radius: var(--st-radius);
  background-color: #fff;
  background-image: linear-gradient(45deg, #e5e7eb 25%, transparent 25%), linear-gradient(-45deg, #e5e7eb 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #e5e7eb 75%), linear-gradient(-45deg, transparent 75%, #e5e7eb 75%);
  background-size: 16px 16px;
  background-position: 0 0, 0 8px, 8px -8px, -8px 0;
  overflow: hidden;
}
.mc-xadrez img {
  display: block;
  width: 100%;
  height: auto;
}

@media (prefers-reduced-motion: reduce) {
  .mc-sk {
    animation: none;
  }
}
@media (max-width: 991.98px) {
  .mc-grid,
  .mc-grid-retangulo .mc-acoes {
    grid-template-columns: minmax(0, 1fr);
  }
  .mc-preview {
    display: flex;
    justify-content: center;
  }
}
@media (max-width: 575.98px) {
  /* Retângulo no celular: reduz o conteúdo para caber (a exportação continua 1920x1080). */
  .mc-retangulo .mc-nome { font-size: 18px; }
  .mc-retangulo .mc-sub { font-size: 11px; }
  .mc-retangulo .mc-carreira { margin-top: 8px; padding: 3px 12px 3px 6px; }
  .mc-retangulo .mc-brasao { width: 38px; }
  .mc-retangulo .mc-carreira-texto strong { font-size: 14px; }
  .mc-retangulo .mc-conquistas { margin-top: 8px; }
  .mc-retangulo .mc-pins img { width: 32px; }
  .mc-retangulo .mc-rodape { margin-top: 8px; }
  .mc-retangulo .mc-logo { width: 80px; }
  .mc-cartao.mc-retangulo { padding-right: 16px; }
}
</style>
